// pages/TeamServicing/TeamServicing.jsx
// REFACTORED - Using shared utilities and constants

import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useToast } from '../../hooks/useToast';
import { ToastContainer } from '../../components/common/Toast';
import { ToolTip} from '../../components/common/ToolTip';

import { Header } from '../../components/team_servicing/Header';
import TimeMetrics from '../../components/team_servicing/sections/TimeMetrics';
import EmployeeSummary from '../../components/team_servicing/sections/EmployeeSummary';
import ProductivityTable from '../../components/team_servicing/sections/ProductivityTable';
import CollapsibleSection from '../../components/common/CollapsibleSection';
import { LoadingSpinner, ErrorMessage, NoDataMessage } from '../../components/common';

import { useProductivityTable } from '../../hooks/team/useProductivityTable';
import { useMonthlyProductivity } from '../../hooks/team/useMonthlyProductivity';
import { useTeamLastUpdated } from '../../hooks/team/useTeamLastUpdated';
import { employeeProcessor } from '../../utils/dataProcessors';

// Import shared utilities and constants
import { 
  getDefaultDateRange, 
  setDocumentMeta, 
  refreshDashboardCache 
} from '../../utils/dashboardUtils';
import { 
  DEFAULT_SECTION_STATE, 
  CACHE_ENDPOINTS, 
  DOCUMENT_TITLES 
} from '../../utils/dashboardConstants';

// ============================================================================
// MEMOIZED COMPONENTS
// ============================================================================

// Cycle Definitions Tooltip Component
const DefinitionsTooltip = () => {
  const KPI_DEFINITIONS = {
    "Productivity": "Defined as Client Hours divided by Available Hours (Total Hours minus Vacation, Sick, and Holiday time). Overall Productivity = Total Client Hours ÷ Total Available Hours. Available Hours = Total Hours - Time Off (Vacation, Sick, Holiday, PTO)",
  };

  return <ToolTip definitions={KPI_DEFINITIONS} width="w-96" />;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const TeamServicingDashboard = () => {
  // Toast notifications
  const { toasts, removeToast } = useToast();

  // Date range state - use shared utility
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  
  // Modal state
  const [showChartModal, setShowChartModal] = useState(false);
  const [selectedMetricData, setSelectedMetricData] = useState(null);
  
  // Section collapse state - use shared constant
  const [sectionState, setSectionState] = useState(DEFAULT_SECTION_STATE);

  // ============================================================================
  // DATA HOOKS
  // ============================================================================
  
  // Productivity table data (priority 1 - loads first)
  const {
    entries: timeData,
    summaryStats,
    loading: tableLoading,
    refreshing: tableRefreshing,
    error: tableError,
    initialized: tableInitialized,
    refetch: refetchTable,
  } = useProductivityTable(dateRange?.start_date, dateRange?.end_date);

  // Monthly productivity trends (priority 2 - loads after table)
  const {
    monthlyTrends,
    loading: trendsLoading,
    refreshing: trendsRefreshing,
    error: trendsError,
    refetch: refetchTrends,
  } = useMonthlyProductivity(dateRange?.start_date, dateRange?.end_date);

  // Last updated timestamp
  const { lastUpdated } = useTeamLastUpdated();

  // ============================================================================
  // HANDLERS
  // ============================================================================
  
  // Section state updater
  const updateSectionState = useCallback((key, value) => {
    setSectionState(prev => ({ ...prev, [key]: value }));
  }, []);

  // Modal handlers
  const handleCardClick = useCallback((title, value, timeData, metricKey, chartType, color) => {
    setSelectedMetricData({
      title,
      value,
      timeData,
      metricKey,
      chartType,
      color
    });
    setShowChartModal(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setShowChartModal(false);
    setSelectedMetricData(null);
  }, []);

  // Date range change
  const handleDateRangeChange = useCallback((newDateRange) => {
    setDateRange(newDateRange);
  }, []);

  // Global refresh with cache clearing - use shared utility
  const handleGlobalRefresh = useCallback(async () => {
    await refreshDashboardCache(
      CACHE_ENDPOINTS.TEAM_SERVICING,
      [refetchTable, refetchTrends]
    );
  }, [refetchTable, refetchTrends]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  
  // Processed employee data
  const processedData = useMemo(() => {
    if (!timeData || timeData.length === 0) {
      return {
        employeeSummary: [],
        employeeCustomerMatrix: {}
      };
    }

    return {
      employeeSummary: employeeProcessor.calculateSummary(timeData),
      employeeCustomerMatrix: employeeProcessor.calculateMatrix(timeData).matrix
    };
  }, [timeData]);

  // Combined loading states
  const loading = tableLoading || trendsLoading;
  const refreshing = tableRefreshing || trendsRefreshing;
  const error = tableError || trendsError;
  const initialized = tableInitialized;

  // ============================================================================
  // EFFECTS
  // ============================================================================
  
  // Set document title and favicon - use shared utility
  useEffect(() => {
    setDocumentMeta(DOCUMENT_TITLES.TEAM);
  }, []);

  // ============================================================================
  // RENDER GUARDS
  // ============================================================================
  
  // Initial loading state (show spinner only if no cached data)
  if (tableLoading && !initialized) {
    return <LoadingSpinner />;
  }

  // Error state (only if no cached data)
  if (error && !timeData) {
    return <ErrorMessage message={error} onRetry={handleGlobalRefresh} />;
  }

  // No data state
  if (!loading && !timeData) {
    return (
      <NoDataMessage 
        title="No Team Productivity Data"
        message="No team productivity data found for the selected date range."
        onRetry={handleGlobalRefresh}
      />
    );
  }
  
  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  
  return (
    <div className="page-wrapper">
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="layout-container responsive-space">
        <Header 
          loading={refreshing}
          lastUpdated={lastUpdated}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          onRefresh={handleGlobalRefresh} 
        />
        
        {/* Main Content */}
        <div className="responsive-space">
          {/* Main Productivity Table - Employee x Customer Matrix */}
          <CollapsibleSection
            title ={
              <div className="flex items-center gap-2">
                <span>Productivity Table</span>
                <DefinitionsTooltip />
              </div>
              }
            subtitle="Hours worked by employee for each customer"
            collapsed={sectionState.productivityCollapsed}
            setCollapsed={(value) => updateSectionState('productivityCollapsed', value)}
          >
            <TimeMetrics 
              summaryStats={summaryStats} 
              onCardClick={handleCardClick}
              dateRange={dateRange}
            />
            <ProductivityTable 
              data={timeData}
              dateRange={dateRange}
              employeeCustomerMatrix={processedData.employeeCustomerMatrix}
            />
          </CollapsibleSection>

          {/* Employee Performance Summary */}
          <CollapsibleSection
            title="Employee Performance"
            subtitle="Hours worked and productivity by employee"
            collapsed={sectionState.employeeCollapsed}
            setCollapsed={(value) => updateSectionState('employeeCollapsed', value)}
          >
            <EmployeeSummary 
              data={timeData} 
              summaryStats={summaryStats}
              onCardClick={handleCardClick}
              employeeSummary={processedData.employeeSummary}
            />
          </CollapsibleSection>
        </div>
      </div>
    </div>
  );
};

// Memoize the entire component
export default memo(TeamServicingDashboard);